import {
  List,
  ListItem,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import CustomCheckIconButton from "shared/components/CustomCheckIconButton";
import { numberInputStyle } from "styles/tts-custom-editor-styles";
import Units from "../Units";

interface CustomMenuProps {
  customHeader?: string;
  customTextFieldPlaceholder: string;
  handleClick: (value: string) => void;
  isNumberType?: boolean;
  showBorder?: boolean;
  allowNegativeCustomValue?: boolean;
  allowMathSymbols?: boolean;
  unitOptions?: string[];
  selectedUnit?: string;
  setSelectedUnit?: (value: string) => void;
  unitPlaceholders?: { unit: string; placeholder: string }[];
  textFieldWidth?: string;
  showCheckButton?: boolean;
  defaultValue?: string;
  value?: string;
  isCustomValueActive?: boolean;
}

export default function CustomMenu({
  customHeader,
  customTextFieldPlaceholder,
  handleClick,
  unitOptions,
  isNumberType,
  showBorder = true,
  allowNegativeCustomValue = false,
  allowMathSymbols = true,
  selectedUnit,
  setSelectedUnit,
  unitPlaceholders,
  textFieldWidth = "250px",
  showCheckButton = false,
  defaultValue = "",
  value,
  isCustomValueActive = false,
}: CustomMenuProps): JSX.Element {
  const [inputValue, setInputValue] = useState<string>(value || defaultValue);
  const classes = numberInputStyle();

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const style = {
    padding: "0 !important",
    backgroundColor: "#ffffff !important",
    "& .MuiOutlinedInput-root": {
      width: textFieldWidth,
      height: "40px",
      borderRadius: 0,
      paddingLeft: 0,
      "&.Mui-focused fieldset": {
        borderColor: "primary",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
      borderRadius: 0,
      "&::placeholder": {
        color: "text.disabled",
      },
    },
  };

  const handleSubmit = () => {
    if (!inputValue.trim()) return;
    if (!allowNegativeCustomValue && parseFloat(inputValue) <= 0) return;
    handleClick?.(inputValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // do not allow mathematical constant or symbols if allowMathSymbols is false
    if (
      !allowMathSymbols &&
      (event.key === "e" ||
        event.key === "E" ||
        event.key === "-" ||
        event.key === "+")
    ) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const handleBlur = () => {
    if (!showCheckButton && inputValue.trim()) {
      handleClick(inputValue);
    }
  };

  return (
    <List
      sx={{
        width: "100%",
        borderTop: showBorder ? "1px solid #E0E0E0" : "none",
        pb: 0,
      }}
      component="nav"
      aria-labelledby="nested-list-subheader"
      subheader={
        customHeader && (
          <ListSubheader
            component="div"
            id="nested-list-subheader"
            sx={{
              pl: isCustomValueActive ? "36px" : "16px",
              position: "relative",
              "&::before": isCustomValueActive
                ? {
                    content: '""',
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "rgba(0, 134, 103, 1)",
                    borderRadius: "50%",
                  }
                : {},
            }}
          >
            <Typography variant="subtitle2" sx={{ py: 1 }}>
              {customHeader}
            </Typography>
          </ListSubheader>
        )
      }
    >
      <ListItem
        sx={{
          padding: customHeader ? "8px 16px" : "0 16px",
          position: "relative",
        }}
      >
        <Stack direction="row" gap={1} width="100%">
          <Stack direction="row" gap={0.5}>
            {unitOptions && selectedUnit && setSelectedUnit && (
              <Units
                options={unitOptions}
                selectedUnit={selectedUnit}
                setSelectedUnit={setSelectedUnit}
              />
            )}
            <TextField
              id="custom-value"
              type={isNumberType ? "number" : "text"}
              className={classes.input}
              onKeyDown={handleKeyDown}
              onChange={(event) => setInputValue(event.target.value)}
              onBlur={handleBlur}
              sx={style}
              placeholder={
                unitPlaceholders
                  ? unitPlaceholders.filter(
                      (obj) => obj.unit === selectedUnit
                    )[0].placeholder
                  : customTextFieldPlaceholder
              }
              variant="outlined"
              value={inputValue}
            />
          </Stack>
          {showCheckButton && (
            <CustomCheckIconButton
              handleSubmit={handleSubmit}
              currentInputValue={inputValue}
            />
          )}
        </Stack>
      </ListItem>
    </List>
  );
}
